/**
 * Notification Priority Mapping
 * Maps event types to priority levels for display
 */

/**
 * Event priority mapping
 * Maps all event types to their priority levels
 */
export const EVENT_PRIORITY_MAP = {
  // ===== URGENT =====
  'system.alert': {
    priority: 'urgent',
    color: 'red',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 8,
  },
  'system.maintenance': {
    priority: 'urgent',
    color: 'red',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 8,
  },
  'system.emergency': {
    priority: 'urgent',
    color: 'red',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 8,
  },

  // ===== HIGH =====
  'pa.approved': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'pa.rejected': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'pa.flagged': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'pa.query_responded': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'claim.paid': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'claim.approved': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'claim.rejected': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },
  'appointment.cancelled': {
    priority: 'high',
    color: 'orange',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 5,
  },

  // ===== MEDIUM =====
  'appointment.booked': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },
  'appointment.confirmed': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },
  'appointment.converted': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },
  'pa.created': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },
  'encounter.created': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },
  'encounter.updated': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  },

  // ===== LOW =====
  'appointment.expired': {
    priority: 'low',
    color: 'gray',
    showBadge: false,
    sound: undefined,
    autoDismiss: 3,
  },
  'system.broadcast': {
    priority: 'low',
    color: 'gray',
    showBadge: false,
    sound: undefined,
    autoDismiss: 3,
  },
  'system.app_update': {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: undefined,
    autoDismiss: 5,
  },
};

/**
 * Get priority configuration for an event type
 * 
 * @param eventType - The event type (e.g., 'pa.approved')
 * @returns Priority configuration object
 */
export function getNotificationPriority(eventType) {
  return EVENT_PRIORITY_MAP[eventType] || {
    priority: 'medium',
    color: 'blue',
    showBadge: true,
    sound: 'alert.wav',
    autoDismiss: 4,
  };
}

/**
 * Get priority level for an event type
 * 
 * @param eventType - The event type
 * @returns Priority level ('low', 'medium', 'high', or 'urgent')
 */
export function getPriorityLevel(eventType) {
  return getNotificationPriority(eventType).priority;
}

/**
 * Get color for priority level
 * 
 * @param priority - Priority level
 * @returns Color name for UI ('red', 'orange', 'blue', or 'gray')
 */
export function getPriorityColor(priority) {
  const colorMap = {
    urgent: 'red',
    high: 'orange',
    medium: 'blue',
    low: 'gray',
  };
  return colorMap[priority];
}

/**
 * Get hex color code for priority level
 * 
 * @param priority - Priority level
 * @returns Hex color code
 */
export function getPriorityHexColor(priority) {
  const colorMap = {
    urgent: '#ff4d4f',
    high: '#faad14',
    medium: '#1890ff',
    low: '#8c8c8c',
  };
  return colorMap[priority];
}

/**
 * Check if a notification should show a badge
 * 
 * @param eventType - The event type
 * @returns true if badge should be shown
 */
export function shouldShowBadge(eventType) {
  return getNotificationPriority(eventType).showBadge;
}

/**
 * Get auto-dismiss duration for a notification
 * 
 * @param eventType - The event type
 * @returns Duration in seconds
 */
export function getAutoDismissDuration(eventType) {
  return getNotificationPriority(eventType).autoDismiss || 4;
}

/**
 * Get sound file for a notification
 * 
 * @param eventType - The event type
 * @returns Sound file name or undefined
 */
export function getNotificationSound(eventType) {
  return getNotificationPriority(eventType).sound;
}

/**
 * Priority level order (for sorting)
 */
export const PRIORITY_ORDER = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

/**
 * Compare two notifications by priority
 * 
 * @param a - First notification
 * @param b - Second notification
 * @returns Negative if a has higher priority, positive if b has higher priority
 */
export function compareByPriority(a, b) {
  return PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority];
}

/**
 * Sort notifications by priority (highest first)
 * 
 * @param notifications - Array of notifications to sort
 * @returns Sorted array
 */
export function sortByPriority(notifications) {
  return [...notifications].sort(compareByPriority);
}

/**
 * Get priority statistics from notifications array
 * 
 * @param notifications - Array of notifications
 * @returns Breakdown by priority level
 */
export function getPriorityStatistics(notifications) {
  return notifications.reduce((stats, notif) => {
    stats[notif.priority]++;
    return stats;
  }, {
    urgent: 0,
    high: 0,
    medium: 0,
    low: 0,
  });
}


